import type { ContentBlockData } from "./block.types";
import type { IStyleLegendMetadata, IVectorStyle } from "../../../shared/model/vector-style-definition.interface";

/**
 * Closed `LayerType` vocabulary of the backend `data.layer` contract (evidence-layer-ui
 * US2 / backend evidence-layer-contract S2) — a new, clean enum decoupled from the legacy
 * `_geojson`/pipeline output-type vocabulary. Unknown or missing `type` resolves to
 * `"default"`. `ortho_query` and `places` are reserved: neither has a backend producer today.
 */
export type LayerType =
  | "geofencing"
  | "workarea"
  | "places"
  | "embeddings"
  | "elements"
  | "property"
  | "structure"
  | "damage"
  | "events_collections"
  | "ortho_collections"
  | "oriented_collections"
  | "ortho_query"
  | "oriented_query"
  | "default";

/**
 * The grouped `data.layer` object the backend attaches to mappable evidence blocks
 * (evidence-layer-ui US1 / backend evidence-layer-contract S1) — everything the UI needs
 * to add the block as a layer. On the wire it sits at `UnifiedBlock.value.data.layer`,
 * i.e. a key of the block's `ContentBlockData` payload. All four keys are always present
 * backend-side (null when absent); `entity` duplicates `metadata.entity` and `plan_ref`
 * duplicates `source.plan_id` by design. `label` overrides the layer name, falling back
 * to `data.label`. A block without the `layer` key keeps today's exact behavior.
 */
export interface LayerContract {
  type: LayerType;
  plan_ref: string | null;
  entity: string | null;
  label: string | null;
}

/** Runtime mirror of the closed `LayerType` vocabulary, for validation on raw wire data. */
const LAYER_TYPES: ReadonlySet<string> = new Set<string>([
  "geofencing",
  "workarea",
  "places",
  "embeddings",
  "elements",
  "property",
  "structure",
  "damage",
  "events_collections",
  "ortho_collections",
  "oriented_collections",
  "ortho_query",
  "oriented_query",
  "default",
]);

/**
 * Resolves the `data.layer` evidence-layer contract off a content block's data payload.
 * Reads ONLY the `layer` key — no id-prefix detection, no legacy metadata/source fallback
 * (evidence-layer-ui US5). Rules:
 * - `layer` absent (or not a plain object) → `undefined`: the block renders exactly as
 *   today (regular card, no layer).
 * - `type` missing, non-string, or outside the closed vocabulary → `"default"`.
 * - `plan_ref`/`entity`/`label` pass through as strings; any other shape (missing, null,
 *   non-string) is absent-data → `null`, never an error (US1).
 */
export function resolveLayerContract(block: ContentBlockData): LayerContract | undefined {
  const raw: unknown = (block as ContentBlockData | null | undefined)?.layer;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const record = raw as Record<string, unknown>;
  const rawType = record["type"];
  const type: LayerType =
    typeof rawType === "string" && LAYER_TYPES.has(rawType) ? (rawType as LayerType) : "default";
  return {
    type,
    plan_ref: asNullableString(record["plan_ref"]),
    entity: asNullableString(record["entity"]),
    label: asNullableString(record["label"]),
  };
}

/** Strings pass through; every other shape (missing, null, non-string) is absent-data → null. */
function asNullableString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

/**
/**
 * `LayerType` → registered OL `styleFunctionName` (evidence-layer-ui T2 + style redesign).
 * Pipeline-backed types reuse the proven `AiLayerStyles` style functions and their
 * feature-properties conventions (`_footprint_type`/`_roof_color`/`_structure_index`,
 * `score`/`scoreColor`, dashed geofence polygons, zone/poi AOI styling); property,
 * damage and structure reuse `propertyPipelineStyle` so they keep the pipeline's
 * formatted data and properties-panel behavior. Workarea renders as a translucent gray
 * veil (fill-only shadow) and the collections family gets a thin-bordered translucent
 * style with a zoom-gated `collection` label. Every remaining type falls back to the
 * softened neutral geometry-typed `defaultEvidenceStyle` registered in
 * `AiLayerStyles.applyStyleFunctions`. Groups key off `layer.plan_ref`, not off this
 * dict (US4) — the legacy `OUTPUT_TYPE_TO_LAYER_GROUP` is NOT extended.
 */
export const LAYER_TYPE_TO_STYLE_FN: Record<LayerType, string> = {
  geofencing: "geofencePipelineStyle",
  workarea: "evidenceWorkareaStyle",
  places: "placesStyle",
  embeddings: "embeddingsPipelineStyle",
  elements: "defaultEvidenceStyle",
  property: "propertyPipelineStyle",
  structure: "propertyPipelineStyle",
  damage: "propertyPipelineStyle",
  events_collections: "evidenceCollectionsStyle",
  ortho_collections: "evidenceCollectionsStyle",
  oriented_collections: "evidenceCollectionsStyle",
  ortho_query: "evidenceCollectionsStyle",
  oriented_query: "evidenceCollectionsStyle",
  default: "defaultEvidenceStyle",
};

/**
 * `LayerType` → humanized display label (evidence-layer-ui T2). Casing follows the
 * type-chip `DEFAULT_LABELS` convention (type chips render uppercase); underscores in
 * compound values become spaces (`events_collections` → "EVENTS COLLECTIONS").
 */
export const LAYER_TYPE_TO_LABEL: Record<LayerType, string> = {
  geofencing: "GEOFENCING",
  workarea: "WORKAREA",
  places: "PLACES",
  embeddings: "EMBEDDINGS",
  elements: "ELEMENTS",
  property: "PROPERTY",
  structure: "STRUCTURE",
  damage: "DAMAGE",
  events_collections: "EVENTS COLLECTIONS",
  ortho_collections: "ORTHO COLLECTIONS",
  oriented_collections: "ORIENTED COLLECTIONS",
  ortho_query: "ORTHO QUERY",
  oriented_query: "ORIENTED QUERY",
  default: "DEFAULT",
};

/** Group descriptor of an `EvidenceLayerRequest` — stable identity plus an optional display name. */
export interface EvidenceLayerRequestGroup {
  /**
   * Stable group identity the map side upserts on (evidence-layer-ui US4). For contract-driven
   * adds it is `"plan:<plan_ref>"`; it must never be a per-call random id.
   */
  key: string;
  /** Display name; the map side derives a deterministic fallback from the key when absent. */
  name?: string;
  /**
   * Full human-readable description (e.g. the plan's untruncated `request_objective`) kept for
   * tooltip consumers; the display `name` may be truncated to avoid deforming the layer tree.
   */
  description?: string;
}

/**
 * Chat-side, map-state-free description of one evidence layer add (evidence-layer-ui US3/US4).
 * Built by `EvidenceLayerService.buildRequest` from a block's `data.layer` contract and carried
 * verbatim through the `add_evidence_layer` `MapEvent`; the map side turns it into the real
 * `Viewerlayer` records. `name` is `layer.label ?? data.label` — absent so the map side falls
 * back to the collection name; `group` is present only when the contract carries a non-null
 * `plan_ref`.
 */
export interface EvidenceLayerRequest {
  name?: string;
  styleFunctionName: string;
  group?: EvidenceLayerRequestGroup;
}

/**
 * Chat-side description of one evidence layer UPDATE (map-layer-block-ui S5). Built by the
 * map-layer card's UPDATE action from a block's `data.map_layer` contract plus the fetched
 * canonical FeatureCollection (already prepared by `FileReaderService.prepareGeoJson`) and
 * carried verbatim through the `update_evidence_layer` `MapEvent`. The map side resolves the
 * live layer by `layer_id` (get_layer_geometry lineage, matching `overlay_layers[].id`) and
 * replaces its data in place.
 *
 * `style`/`style_metadata` are optional and carried straight from the block's inline
 * `map_layer` contract: an unstyled update is a normal, expected state that keeps the layer's
 * current styling — never an error.
 */
export interface EvidenceLayerUpdateRequest {
  /** Id of the existing user layer to update; present only when the block carries `layer_id`. */
  layer_id: string;

  /** Prepared FeatureCollection (FileReaderService.prepareGeoJson output) replacing the layer's features. */
  collection: any;

  /** New display name; the layer keeps its current name when absent. */
  name?: string;

  /** Optional vector style definition, applied map-side via `updateLayerStyleDefinition` when provided. */
  style?: IVectorStyle;

  /** Optional style legend metadata persisted alongside the style definition. */
  style_metadata?: IStyleLegendMetadata;
}
